import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Form, Modal } from 'react-bootstrap';
import { Users, Eye, HandCoins } from 'lucide-react';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { Pagination } from '../../features/Pagination';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';

const PAGE_SIZE = 20;

const parseStatus = (value) => {
  const num = Number.parseInt(value, 10);
  return Number.isInteger(num) ? num : null;
};

const WithdrawalRequests = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);
  // Применённые поиск и статус — в URL
  const query = searchParams.get('q') || '';
  const status = parseStatus(searchParams.get('status'));

  const [requests, setRequests] = useState({ items: [], totalCount: 0 });
  // Черновики в полях
  const [searchValue, setSearchValue] = useState(query);
  const [filterValues, setFilterValues] = useState({ status });
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => setSearchValue(query), [query]);
  useEffect(() => setFilterValues({ status }), [status]);

  // Модалка для просмотра/редактирования заявки
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [comment, setComment] = useState('');

  const statusOptions = [
    { value: null, label: 'Все заявки' },
    { value: 1, label: 'Ожидание' },
    { value: 2, label: 'Выплачено' },
    { value: 3, label: 'Отменено' },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return <Badge bg="warning">Ожидание</Badge>;
      case 2:
        return <Badge bg="success">Выплачено</Badge>;
      case 3:
        return <Badge bg="danger">Отменено</Badge>;
      default:
        return <Badge bg="secondary">Неизвестно</Badge>;
    }
  };

  const getStatusLabel = (status) => {
    const option = statusOptions.find((opt) => opt.value === status);
    return option ? option.label : 'Неизвестно';
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequests.referralProgram
      .getWithdrawalRequests(status, null, query, pageSize, offset)
      .then((response) => {
        if (!cancelled) setRequests(response);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Ошибка загрузки заявок:', e);
        toast.error('Ошибка загрузки заявок');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, status, reloadKey]);

  // Записать поиск/фильтр в URL и вернуться на первую страницу
  const applyParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') params.delete(key);
        else params.set(key, String(value));
      });
      params.delete('page');
      return params;
    });
  };

  const handleSearch = () => applyParams({ q: searchValue.trim(), status: filterValues.status });

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      const intValue = value === null ? null : parseInt(value);
      setFilterValues((prev) => ({ ...prev, [key]: intValue }));
    } else {
      setFilterValues((prev) => ({ ...prev, [key]: value }));
    }
  };

  const applyFilters = handleSearch;

  const resetFilters = () => applyParams({ q: null, status: null });

  const handleOpenModal = async (request) => {
    try {
      const fullRequest = await apiRequests.referralProgram.getWithdrawalRequest(request.id);
      setSelectedRequest(fullRequest);
      setNewStatus(fullRequest.status);
      setComment(fullRequest.comment || '');
      setShowModal(true);
    } catch (e) {
      console.error('Ошибка загрузки заявки:', e);
      toast.error('Ошибка загрузки заявки');
    }
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;

    try {
      await apiRequests.referralProgram.updateWithdrawalRequest({
        id: selectedRequest.id,
        status: newStatus,
        comment: comment,
      });
      toast.success('Заявка обновлена');
      setShowModal(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      console.error('Ошибка обновления заявки:', e);
      toast.error(e.response?.data?.error || 'Ошибка обновления заявки');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (value, currencySymbol = '$') => {
    return `${value.toFixed(2)} ${currencySymbol}`;
  };

  const filters = [
    {
      key: 'status',
      type: 'select',
      label: 'Статус',
      value: filterValues.status === null ? null : String(filterValues.status),
      placeholder: 'Все заявки',
      options: statusOptions.filter((o) => o.value !== null).map((o) => ({ value: String(o.value), label: o.label })),
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск по комментарию..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        rightContent={
          <Link to="/referral-program/members" className="page-bar-clean__link-btn">
            <Users size={18} />
            <span>Участники</span>
          </Link>
        }
      />

      <div className="mb-3">
        {loading ? (
          <p className="text-center">Загрузка...</p>
        ) : requests.items?.length === 0 ? (
          query || status !== null ? (
            <EmptyState
              icon={HandCoins}
              variant="search"
              title="Подходящих заявок нет"
              description="Ни одна заявка не совпадает с запросом и статусом. Поиск идёт по комментарию — попробуйте другое слово или сбросьте фильтр."
            />
          ) : (
            <EmptyState
              icon={HandCoins}
              title="Заявок на выплату пока нет"
              description="Когда участник программы запросит вывод вознаграждения, заявка появится здесь — останется проверить её и отметить выплату."
            />
          )
        ) : (
          requests.items?.map((request) => {
            const statusVariant = request.status === 1 ? 'warning' : request.status === 2 ? 'success' : 'danger';
            return (
              <EntityCard
                key={request.id}
                title={`Заявка #${request.id}`}
                subtitle={`Счет: ${request.accountFrom?.currency?.title || 'Не указан'}`}
                badge={{ text: getStatusLabel(request.status), variant: statusVariant }}
                fields={[
                  { label: 'Сумма', value: formatCurrency(request.transaction?.sum || 0, request.accountFrom?.currency?.symbol) },
                  { label: 'Дата', value: formatDate(request.createdAt) },
                ]}
                actions={
                  <button
                    onClick={() => handleOpenModal(request)}
                    className="entity-card__action"
                    title="Просмотр и редактирование"
                  >
                    <Eye size={20} />
                  </button>
                }
              />
            );
          })
        )}
      </div>

      <Pagination
        page={page}
        total={requests.totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
      />

      {/* Модалка просмотра/редактирования заявки */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Заявка на выплату #{selectedRequest?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <div className="mb-3">
                <strong>Текущий статус:</strong> {getStatusBadge(selectedRequest.status)}
              </div>

              <div className="mb-3">
                <strong>Счет:</strong> {selectedRequest.accountFrom?.currency?.title} (
                {selectedRequest.accountFrom?.currency?.symbol})
              </div>

              <div className="mb-3">
                <strong>Сумма:</strong>{' '}
                {formatCurrency(
                  selectedRequest.transaction?.sum || 0,
                  selectedRequest.accountFrom?.currency?.symbol,
                )}
              </div>

              <div className="mb-3">
                <strong>Дата создания:</strong> {formatDate(selectedRequest.createdAt)}
              </div>

              {selectedRequest.transaction?.description && (
                <div className="mb-3">
                  <strong>Описание транзакции:</strong>{' '}
                  {selectedRequest.transaction.description}
                </div>
              )}

              <hr />

              {selectedRequest.status === 1 && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Изменить статус</Form.Label>
                    <Form.Select
                      value={newStatus}
                      onChange={(e) => setNewStatus(parseInt(e.target.value))}
                    >
                      {statusOptions
                        .filter((opt) => opt.value !== null)
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Комментарий</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Добавьте комментарий к заявке"
                    />
                  </Form.Group>
                </>
              )}

              {selectedRequest.status !== 1 && (
                <div className="alert alert-info">
                  Заявка уже обработана. Редактирование невозможно.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Закрыть
          </Button>
          {selectedRequest?.status === 1 && (
            <Button variant="primary" onClick={handleUpdateRequest}>
              Сохранить изменения
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WithdrawalRequests;
